import { AfterContentChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output } from "@angular/core";
import dayjs from "@gtmhub/dayjs";
import { CheckInTemplate } from "@webapp/reflections/models/checkins-models";
import { ReflectionPanel } from "@webapp/reflections/models/reflection-panel.models";
import {
  ReflectionAnswerChange,
  ReflectionMoodChange,
  ReflectionNoteChange,
  ReflectionOrder,
  ReflectionQuestion,
} from "@webapp/reflections/models/reflection-question.model";
import { ReflectionMoodType, ReflectionNote, ReflectionOverviewFilterName } from "@webapp/reflections/models/reflections.models";
import { getLastMondayAsDate } from "@webapp/reflections/utils";
import { isReflectionEmpty } from "@webapp/reflections/utils/is-reflection-empty";

@Component({
  selector: "reflection-compare",
  templateUrl: "./reflection-compare.component.html",
  styleUrls: ["./reflection-compare.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReflectionCompareComponent implements OnInit, OnChanges, AfterContentChecked {
  @Input() public currReflection?: ReflectionPanel;
  @Input() public prevReflection?: ReflectionPanel;
  @Input() public filter: ReflectionOverviewFilterName;
  @Input() public currCadenceStartDate: string;
  @Input() public prevCadenceStartDate: string;
  @Input() public checkInTemplate: CheckInTemplate;

  @Output() public readonly moodUpdate = new EventEmitter<ReflectionMoodChange>();
  @Output() public readonly noteUpdate = new EventEmitter<ReflectionNoteChange>();
  @Output() public readonly answerUpdate = new EventEmitter<ReflectionAnswerChange>();
  @Output() public readonly fillOut = new EventEmitter<ReflectionOrder>();
  @Output() public readonly edit = new EventEmitter<ReflectionOrder>();
  @Output() public readonly cancelEdit = new EventEmitter<ReflectionOrder>();
  @Output() public readonly publish = new EventEmitter<ReflectionOrder>();

  public isReflectionEmpty = isReflectionEmpty;
  public isCurrentCadence: boolean;
  public currQuestionsFormatted: ReflectionQuestion[];
  public prevQuestionsFormatted: ReflectionQuestion[];

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.currQuestionsFormatted = this.formatCurrQuestions();
    this.prevQuestionsFormatted = structuredClone(this.formatPrevQuestions());
  }

  public ngOnChanges(): void {
    this.isCurrentCadence = dayjs(this.currCadenceStartDate).isSame(getLastMondayAsDate());
  }

  public ngAfterContentChecked(): void {
    this.cdr.detectChanges();
  }

  public onMoodChange(mood: ReflectionMoodType, type: ReflectionOrder): void {
    this.moodUpdate.emit({ type, mood });
  }

  public onNoteChange(change: ReflectionNote, type: ReflectionOrder, index: number): void {
    this.noteUpdate.emit({ type, note: change, noteIndex: index });
  }

  public onAnswerChange(question: ReflectionQuestion, type: ReflectionOrder): void {
    this.answerUpdate.emit({ type, question });
  }

  public onEditReflection(type: ReflectionOrder): void {
    this.edit.emit(type);
  }

  public onCancelReflection(type: ReflectionOrder): void {
    this.cancelEdit.emit(type);
    this.cdr.detectChanges();
  }

  public onPublishReflection(type: ReflectionOrder): void {
    this.publish.emit(type);
  }

  public onFillOutReflection(type: ReflectionOrder): void {
    this.fillOut.emit(type);
  }

  public shouldRenderMood(checkIn: ReflectionPanel): boolean {
    return checkIn?.settings?.moodsEnabled || (!checkIn?.id && this.checkInTemplate.moodsEnabled);
  }

  public shouldRenderNote(checkIn: ReflectionPanel, note: ReflectionNote): boolean {
    return (checkIn?.settings?.notesEnabled || (!checkIn?.id && this.checkInTemplate.notesEnabled)) && (checkIn?.isEditing || !!note?.text || !!note?.gif?.id);
  }

  public shouldRenderQuestion(checkIn: ReflectionPanel, question: ReflectionQuestion): boolean {
    return (question?.answer || checkIn.isEditing || question?.gif?.id) && question?.isEnabled;
  }

  public shouldRenderCurrentNoCheckIn(): boolean {
    return (!this.currReflection || (this.currReflection.noReflection && (!this.isCurrentCadence || !this.currReflection.isCurrentUser))) && !this.filter;
  }

  public shouldRenderPreviousNoCheckIn(): boolean {
    return (!this.prevReflection || this.prevReflection.noReflection) && !this.filter;
  }

  private formatCurrQuestions(): ReflectionQuestion[] {
    const currQuestionsFormatted = structuredClone(this.currReflection?.questions) || [];

    this.prevReflection?.questions?.forEach((prevQuestion) => {
      const isQuestionMatched = currQuestionsFormatted.some((currQuestion) => currQuestion?.id === prevQuestion?.id);
      if (!isQuestionMatched) {
        currQuestionsFormatted.push(undefined);
      }
    });

    return currQuestionsFormatted;
  }

  private formatPrevQuestions(): ReflectionQuestion[] {
    const prevQuestions: ReflectionQuestion[] = [];
    const currQuestions = this.currReflection?.questions || [];

    currQuestions.forEach((currQuestion) => {
      prevQuestions.push(this.prevReflection?.questions?.find((prevQuestion) => prevQuestion?.id === currQuestion?.id));
    });

    this.prevReflection?.questions?.forEach((prevQuestion) => {
      const isQuestionMatched = currQuestions.some((currQuestion) => currQuestion?.id === prevQuestion?.id);
      if (!isQuestionMatched) {
        prevQuestions.push(prevQuestion);
      }
    });

    return prevQuestions;
  }
}
